import type {
  MessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { createContext } from "react";
import type { ConferenceParticipant } from "./use-voice-actions";

/**
 * Status of the per-panel Telnyx WebRTC session.
 */
export type ConnectionStatus =
  | "disconnected"
  | "fetching-token"
  | "connecting"
  | "ready"
  | "error";

export type MicPermission = "unknown" | "granted" | "prompt" | "denied";

export type AvailabilityStatus = "available" | "in-call";

/**
 * Lightweight shape of an active SDK Call as used by the panel UI. We
 * keep this loose because the @telnyx/webrtc Call type is broad and the
 * panel only touches a few fields. Anything we expose here is what the
 * provider already typed when it captured the call.
 */
export interface TelnyxCallLike {
  hangup: () => void;
  answer: () => void;
  readonly id?: string;
  readonly callID?: string;
  readonly callId?: string;
  readonly state?: string;
  toggleAudioMute?: () => void;
  muteAudio?: () => void;
  setAudioInDevice?: (deviceId: string) => Promise<void>;
  toggleDeaf?: () => void;
  toggleHold?: () => void;
  dtmf?: (digit: string) => void;
  readonly remoteStream?: MediaStream;
}

/**
 * One incoming call awaiting accept/reject. `messageId` and `fromNumber`
 * are populated from the SIP custom headers
 * (`X-Redo-Message-Id`, `X-Redo-From-Number`) attached to the invite.
 */
export interface IncomingCallV2 {
  readonly messageId: MessageId | null;
  readonly fromNumber: string | null;
  readonly call: TelnyxCallLike;
}

export interface CallCustomer {
  readonly mongoCustomerId: string;
  readonly name: string;
  readonly imageUrl: string | null;
}

/**
 * One active call (post-accept for inbound or post-dial for outbound).
 * `bridged` flips true once we observe inbound RTP — the visual cue that
 * media is actually flowing.
 */
export interface ActiveCallV2 {
  readonly messageId: MessageId | null;
  readonly direction: "inbound" | "outbound";
  readonly remoteNumber: string;
  readonly customer: CallCustomer | null;
  readonly state: string | null;
  readonly startedAt: Date | null;
  readonly bridged: boolean;
  /** Set for a manager-monitor call; "private" locks mute on. */
  readonly managerVisibility: "visible" | "private" | null;
}

export interface DebugTelnyxNotification {
  readonly id: number;
  readonly receivedAt: Date;
  readonly type: string;
  readonly callState: string | null;
  readonly callId: string | null;
  readonly fromNumber: string | null;
  readonly messageId: string | null;
  readonly isDebugPing: boolean;
  readonly customHeaders: readonly {
    readonly name: string;
    readonly value: string;
  }[];
}

/**
 * Public surface every voice-v2 UI consumer reads from. Stable shape so
 * the inner panel (dev harness) and the floating widget (production)
 * share one consumer contract.
 */
export interface VoiceV2State {
  // Connection
  readonly connectionStatus: ConnectionStatus;
  readonly statusDetail: string | null;
  readonly connect: () => void;
  readonly connectForMonitoring: () => Promise<void>;
  readonly disconnect: () => void;
  // Operator intent — true when the user has chosen to be online
  // (clicked "Available"). False on first load and after explicitly
  // choosing "Unavailable". Used to suppress diagnostic badges when
  // the disconnected state is the user's intent, not a fault.
  readonly desiredOnline: boolean;

  // Microphone
  readonly micPermission: MicPermission;
  readonly micError: string | null;
  readonly requestMic: () => void;

  // Calls
  readonly incoming: IncomingCallV2 | null;
  readonly activeCall: ActiveCallV2 | null;
  readonly acceptIncoming: () => void;
  readonly rejectIncoming: () => void;
  readonly placeOutbound: (phoneNumber: string) => void;
  readonly hangup: () => void;
  // True from the moment placeOutbound is invoked until the active
  // call appears (or the dial fails). Used to disable the Call button
  // and surface "Calling…" so impatient operators don't fire a
  // second placeOutbound while the first is still in flight.
  readonly isPlacingOutbound: boolean;

  // Call controls
  readonly muted: boolean;
  readonly deafened: boolean;
  // Deafened on purpose because the agent's own forwarded phone joined.
  readonly deafenedForOwnPhone: boolean;
  // The browser refused to play call audio; resumePlayback retries it.
  readonly playbackBlocked: boolean;
  readonly resumePlayback: () => void;
  readonly onHold: boolean;
  readonly recording: boolean;
  readonly toggleMute: () => void;
  readonly toggleDeafen: () => void;
  readonly toggleHold: () => void;
  readonly toggleRecord: () => void;
  readonly sendDtmf: (digit: string) => void;
  // Audio devices: apply to the live call, then save. False if it failed.
  readonly selectMicrophone: (deviceId: string) => Promise<boolean>;
  readonly selectSpeaker: (deviceId: string) => Promise<boolean>;

  // Availability (server presence)
  readonly availability: AvailabilityStatus | null;

  // Conference participants (live list polled while a call is active —
  // empty when no call). Includes "You" in the form of the matching agent
  // row from the server response. Updates as warm-transfer legs join/
  // leave.
  readonly participants: readonly ConferenceParticipant[];

  // Support ticket the active call is bound to. Resolved server-
  // side from the call's `messageId` through its channel thread. Null
  // until the polling round resolves it. Used to render a "Go to
  // ticket" button while the call is active.
  readonly activeSupportTicketId: SupportTicketId | null;

  // Persisted dialer input — lives in context so the typed number
  // survives collapse/expand of the floating widget. The idle-screen
  // dialer reads/writes this instead of holding its own local state.
  readonly dialerNumber: string;
  readonly setDialerNumber: (value: string) => void;

  // Expanded vs collapsed state of the floating widget. Lifted out of
  // the widget component so external callers (e.g., the support ticket
  // "Call back" button) can pop the widget open programmatically.
  readonly widgetExpanded: boolean;
  readonly setWidgetExpanded: (expanded: boolean) => void;

  // Warm-transfer state — UI hint shown while a transfer is in flight
  // before the new leg appears in the participants list. `targetLabel`
  // is the agent's name or external phone number to render in the
  // "Ringing X..." placeholder row.
  readonly transferRinging: { readonly targetLabel: string } | null;
  readonly addAgentToCall: (
    targetMongoUserId: MongoUserId,
    targetLabel: string,
  ) => void;
  readonly addPhoneNumberToCall: (phoneNumber: string) => void;

  // Impersonation (dev harness only — provider always exposes the setter,
  // but the prod widget hides the picker by passing showImpersonation=false)
  readonly targetMongoUserId: MongoUserId | undefined;
  readonly setTargetMongoUserId: (id: MongoUserId | undefined) => void;

  // Dev harness only. Production does not render this stream.
  readonly debugTelnyxNotifications: readonly DebugTelnyxNotification[];
}

export const VoiceContextV2 = createContext<VoiceV2State | undefined>(
  undefined,
);
